import { cva, type VariantProps } from "class-variance-authority";
import { splitProps, type ComponentProps } from "solid-js";
import { cn } from "~/lib/utils";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-semibold tracking-wide transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 disabled:pointer-events-none disabled:opacity-40 cursor-pointer select-none",
  {
    variants: {
      variant: {
        default:
          "bg-brand text-white hover:bg-brand-dim active:scale-[0.97]",
        outline:
          "border border-border bg-transparent text-text-base hover:bg-surface-3 active:scale-[0.97]",
        ghost:
          "bg-transparent text-text-muted hover:bg-surface-3 hover:text-text-base",
        danger:
          "bg-red-600/90 text-white hover:bg-red-600 active:scale-[0.97]",
        secondary:
          "bg-surface-3 text-text-base border border-border hover:bg-surface-4 active:scale-[0.97]",
        "brand-outline":
          "border border-brand/50 bg-brand/5 text-brand hover:bg-brand/15 hover:border-brand/70 active:scale-[0.97]",
        "danger-outline":
          "border border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10 hover:border-red-500/60 active:scale-[0.97] focus-visible:ring-red-500/40",
      },
      size: {
        sm: "h-7 px-3 text-sm rounded-md",
        md: "h-9 px-4 text-base rounded-md",
        lg: "h-11 px-6 text-lg rounded-md",
        icon: "h-9 w-9 rounded-md",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

type ButtonProps = ComponentProps<"button"> & VariantProps<typeof buttonVariants>;

export function Button(props: ButtonProps) {
  const [local, rest] = splitProps(props, ["class", "variant", "size"]);
  return (
    <button
      class={cn(buttonVariants({ variant: local.variant, size: local.size }), local.class)}
      {...rest}
    />
  );
}
